import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAdmin } from "@/context/AdminContext";
import { mediaUrl } from "@/lib/api";
import { HlsVideo } from "@/components/media/HlsVideo";
import { BLOCK_TYPES, FONTS, PAD, RATIOS, SWATCHES, WIDTHS } from "./blockConfig";
import { MediaUpload } from "./MediaUpload";

const Row = ({ label, children }) => (
  <div className="flex flex-col gap-2 border-b border-black/10 py-4">
    <span className="text-xs font-bold uppercase tracking-wider text-[#57534E]">{label}</span>
    {children}
  </div>
);

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={String(value)} onValueChange={onChange}>
    <SelectTrigger className="rounded-none bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className="z-[90]">{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

const Colour = ({ value, onChange, allowNone }) => (
  <div className="flex flex-wrap items-center gap-2">
    {allowNone && <button onClick={() => onChange("")} className={`h-8 rounded-full border px-3 text-xs ${!value ? "border-black font-bold" : "border-black/20"}`}>None</button>}
    {SWATCHES.map((c) => (
      <button key={c} onClick={() => onChange(c)} aria-label={c} style={{ background: c }} className={`h-8 w-8 rounded-full border ${value === c ? "ring-2 ring-[#E62314] ring-offset-2" : "border-black/20"}`} />
    ))}
    <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-8 w-10 cursor-pointer border-0 bg-transparent" aria-label="Custom colour" />
  </div>
);

const Num = ({ value, onChange, min, max, step = 1, unit = "px" }) => (
  <div className="flex items-center gap-3">
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="flex-1" />
    <span className="w-14 text-right font-mono-label text-xs">{value}{unit}</span>
  </div>
);

const LinkPick = ({ value, onChange }) => {
  const { pages } = useAdmin();
  return (
    <div className="flex flex-col gap-2">
      <Pick value={pages.some((p) => p.path === value) ? value : "_custom"} onChange={(v) => onChange(v === "_custom" ? "" : v)}
        options={{ ...Object.fromEntries(pages.map((p) => [p.path, `Page: ${p.title}`])), _custom: "Other web address" }} />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://..." className="border border-black/20 bg-white px-3 py-2 text-sm outline-none" />
    </div>
  );
};

const TextSettings = ({ b, set }) => (
  <>
    <Row label="Font"><Pick value={b.font} onChange={(v) => set({ font: v })} options={Object.fromEntries(Object.entries(FONTS).map(([k, f]) => [k, f.label]))} testId="settings-font" /></Row>
    <Row label="Size"><Num value={b.size} min={12} max={200} onChange={(v) => set({ size: v })} /></Row>
    <Row label="Weight"><Pick value={b.weight} onChange={(v) => set({ weight: Number(v) })} options={{ 400: "Regular", 600: "Semi bold", 800: "Bold" }} /></Row>
    <Row label="Italic"><Switch checked={b.italic} onCheckedChange={(v) => set({ italic: v })} /></Row>
    <Row label="Text colour"><Colour value={b.color} onChange={(v) => set({ color: v })} /></Row>
  </>
);

const MediaSettings = ({ b, set, kind }) => (
  <>
    <Row label={kind === "image" ? "Photo" : "Video"}>
      {b.media && (kind === "image" ? <img src={mediaUrl(b.media.id)} alt="" className="max-h-40 w-full object-contain bg-[#ECEAE6]" /> : <HlsVideo src={mediaUrl(b.media.id)} className="max-h-40 w-full bg-black" muted />)}
      <MediaUpload accept={kind === "image" ? "image/*" : "video/*"} label={b.media ? "Replace" : "Upload"} onUploaded={(m) => set({ media: m })} testId="settings-upload" />
    </Row>
    <Row label="Shape"><Pick value={b.ratio} onChange={(v) => set({ ratio: v })} options={RATIOS} /></Row>
    {kind === "image" && <>
      <Row label="Rounded corners"><Num value={b.radius} min={0} max={48} onChange={(v) => set({ radius: v })} /></Row>
      <Row label="Caption"><input value={b.caption} onChange={(e) => set({ caption: e.target.value })} className="border border-black/20 bg-white px-3 py-2 text-sm outline-none" /></Row>
      <Row label="Clicking the photo opens"><LinkPick value={b.link} onChange={(v) => set({ link: v })} /></Row>
    </>}
    {kind === "video" && <>
      <Row label="Play automatically (muted, on loop)"><Switch checked={b.autoplay} onCheckedChange={(v) => set({ autoplay: v })} /></Row>
      <Row label="Show play controls"><Switch checked={b.controls} onCheckedChange={(v) => set({ controls: v })} /></Row>
    </>}
  </>
);

const TypeSettings = ({ b, set }) => {
  if (b.type === "heading" || b.type === "text") return <TextSettings b={b} set={set} />;
  if (b.type === "image" || b.type === "video") return <MediaSettings b={b} set={set} kind={b.type} />;
  if (b.type === "gallery") return (
    <>
      <Row label={`Photos & videos (${b.items.length})`}>
        <MediaUpload multiple label="Add photos & videos" onUploaded={(list) => set({ items: [...b.items, ...list] })} testId="settings-gallery-upload" />
        <span className="text-xs text-[#57534E]">Remove items with the ✕ on each one in the page.</span>
      </Row>
      <Row label="Columns"><Num value={b.columns} min={1} max={5} unit="" onChange={(v) => set({ columns: v })} /></Row>
      <Row label="Shape"><Pick value={b.ratio} onChange={(v) => set({ ratio: v })} options={RATIOS} /></Row>
      <Row label="Gap between"><Num value={b.gap} min={0} max={48} onChange={(v) => set({ gap: v })} /></Row>
    </>
  );
  if (b.type === "button") return (
    <>
      <Row label="Button text"><input value={b.label} onChange={(e) => set({ label: e.target.value })} className="border border-black/20 bg-white px-3 py-2 text-sm outline-none" data-testid="settings-button-label" /></Row>
      <Row label="Opens"><LinkPick value={b.link} onChange={(v) => set({ link: v })} /></Row>
      <Row label="Style"><Pick value={b.variant} onChange={(v) => set({ variant: v })} options={{ underline: "Red underline", solid: "Solid block" }} /></Row>
      <Row label="Colour"><Colour value={b.color} onChange={(v) => set({ color: v })} /></Row>
    </>
  );
  if (b.type === "spacer") return <Row label="Height"><Num value={b.height} min={8} max={400} onChange={(v) => set({ height: v })} /></Row>;
  return null;
};

export const BlockSettings = ({ block, onChange, onClose }) => (
  <Sheet open={!!block} onOpenChange={(o) => !o && onClose()}>
    <SheetContent data-lenis-prevent data-testid="block-settings" className="w-full overflow-y-auto bg-[#FBF9F5] sm:max-w-md">
      {block && <>
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl font-extrabold">{BLOCK_TYPES[block.type]?.label} settings</SheetTitle>
          <SheetDescription>Changes show on the page straight away and save automatically.</SheetDescription>
        </SheetHeader>
        <TypeSettings b={block} set={onChange} />
        <Row label="Width on the page"><Pick value={block.width} onChange={(v) => onChange({ width: v })} options={WIDTHS} testId="settings-width" /></Row>
        <Row label="Line up"><Pick value={block.align} onChange={(v) => onChange({ align: v })} options={{ left: "Left", center: "Centre", right: "Right" }} testId="settings-align" /></Row>
        <Row label="Background colour"><Colour value={block.bg} allowNone onChange={(v) => onChange({ bg: v })} /></Row>
        <Row label="Inner spacing"><Pick value={block.pad} onChange={(v) => onChange({ pad: v })} options={{ none: "None", s: "Small", m: "Medium", l: "Large" }} /></Row>
        <p className="py-4 text-xs text-[#57534E]">Tip: put two Half blocks next to each other to place things side by side.</p>
      </>}
    </SheetContent>
  </Sheet>
);

export { PAD };
